/**
 * The running order on the playout screen: rundowns → segments → parts, the
 * On Air and Next parts in their tally colours, each segment's budget
 * against its pace, a queued segment marked, and the segment commands (set
 * next segment now, queue it after the current one).
 *
 * The part cursor (`selectedPartId`) is the keyboard's; the view moves it.
 * A part row scrolls into view when it becomes the cursor or goes on air.
 */
import { useEffect, useRef } from 'react';
import { formatLength, formatOverUnder } from '../../lib/playout/format.js';
import type { PlayoutView, SegmentView } from '../../lib/playout/viewModel.js';
import { cn } from '../../lib/utils.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { PlayoutPartRow } from './PlayoutPartRow.js';

export type PlayoutRundownProps = {
  readonly view: PlayoutView;
  readonly selectedPartId: string | null;
  readonly onAirClock: { readonly elapsedMs: number; readonly remainingMs: number | null } | null;
  /** Next may be set (`playout.take`, an active playlist). */
  readonly canCommand: boolean;
  readonly onSelect: (partId: string) => void;
  readonly onSetNext: (partId: string) => void;
  readonly onSetNextSegment: (segmentId: string) => void;
  readonly onQueueSegment: (segmentId: string) => void;
};

function SegmentHeader({ segment, canCommand, onSetNextSegment, onQueueSegment }: { readonly segment: SegmentView } & Pick<PlayoutRundownProps, 'canCommand' | 'onSetNextSegment' | 'onQueueSegment'>) {
  const overUnder = formatOverUnder(segment.overUnderMs);
  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-muted px-3 py-1.5" data-testid="segment-header">
      <span className="font-semibold" data-testid="segment-name">
        {segment.name}
      </span>
      {segment.hidden ? <Badge variant="outline">Hidden</Badge> : null}
      {segment.queued ? <Badge variant="standby">Queued next</Badge> : null}
      <span className="ml-auto flex items-center gap-3 font-mono text-xs text-muted-foreground tabular-nums" data-testid="segment-timing">
        <span title="Planned length of its parts">Planned {formatLength(segment.expectedMs)}</span>
        <span title="Played in this activation">Played {formatLength(segment.playedMs)}</span>
        {segment.budgetMs === null ? null : (
          <>
            <span title="The segment's budget">Budget {formatLength(segment.budgetMs)}</span>
            <span
              className={cn('rounded px-1', overUnder.tone === 'over' && 'bg-onair text-onair-foreground', overUnder.tone === 'under' && 'bg-next text-next-foreground')}
              data-testid="segment-over-under"
              data-tone={overUnder.tone}
            >
              {overUnder.text}
            </span>
          </>
        )}
      </span>
      {segment.offRunningOrder ? null : (
        <span className="flex gap-1">
          <Button size="sm" variant="ghost" disabled={!canCommand} onClick={() => onSetNextSegment(segment.segmentId)} data-testid="set-next-segment">
            Next segment
          </Button>
          <Button size="sm" variant="ghost" disabled={!canCommand || segment.queued} onClick={() => onQueueSegment(segment.segmentId)} data-testid="queue-segment">
            Queue
          </Button>
        </span>
      )}
    </div>
  );
}

export function PlayoutRundown({ view, selectedPartId, onAirClock, canCommand, onSelect, onSetNext, onSetNextSegment, onQueueSegment }: PlayoutRundownProps) {
  const root = useRef<HTMLDivElement>(null);
  // Keep the cursor, and the part going on air, in view (the browser does nothing when it already is).
  useEffect(() => {
    const id = selectedPartId ?? view.onAirPartId;
    if (id === null) return;
    const row = root.current?.querySelector(`[data-part-id="${CSS.escape(id)}"]`);
    if (row instanceof HTMLElement && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
  }, [selectedPartId, view.onAirPartId]);

  if (view.partOrder.length === 0) {
    return (
      <p className="p-8 text-center text-sm text-muted-foreground" data-testid="playout-rundown-empty">
        The running order is empty: add segments and parts in the rundown editor.
      </p>
    );
  }
  return (
    <div ref={root} className="grid" data-testid="playout-rundown">
      {view.rundowns.map((rundown) => (
        <section key={rundown.rundownId} aria-label={rundown.name} className="grid">
          {view.rundowns.length > 1 ? <h2 className="bg-secondary px-3 py-1 text-sm font-semibold text-secondary-foreground">{rundown.name}</h2> : null}
          {rundown.segments.map((segment) => (
            <div key={segment.segmentId} className={cn('grid border-b', segment.hidden && 'opacity-60')} data-testid="playout-segment" data-segment-id={segment.segmentId}>
              <SegmentHeader segment={segment} canCommand={canCommand} onSetNextSegment={onSetNextSegment} onQueueSegment={onQueueSegment} />
              {segment.parts.map((part) => (
                <PlayoutPartRow
                  key={`${part.partId}:${part.instanceId ?? ''}`}
                  part={part}
                  selected={part.partId === selectedPartId}
                  onAirClock={part.status === 'onair' ? onAirClock : null}
                  canSetNext={canCommand}
                  onSelect={() => onSelect(part.partId)}
                  onSetNext={() => onSetNext(part.partId)}
                />
              ))}
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}
